import React from 'react';
import { Check, Plus, Trash2 } from 'lucide-react';
import * as service from '../../../services/bauordnungService';
import { EntwurfInput } from './EntwurfInput';
import type { SchreibKontext } from '../../../services/bauordnungService';
import { naechsteLueckenNummer, sortiereLuecken } from '../bauordnungLogic';
import type { BauordnungBundle, Luecke } from '../types';

/**
 * Die offenen Punkte — der Sicherheitsmechanismus des Moduls.
 *
 * Nummern werden NIE neu vergeben: eine geloeschte Luecke hinterlaesst eine
 * Zaehluecke, sonst wird jeder aeltere Verweis auf „Nr. 4" falsch. Erledigte
 * bleiben in der Liste stehen, sie verschwinden nicht — was einmal offen war,
 * gehoert zur Geschichte des Dokuments.
 */

interface Props {
    bundle: BauordnungBundle;
    ctx: SchreibKontext;
    gesperrt: boolean;
    schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
}

const eingabe = 'text-[13px] border border-slate-200 rounded-lg px-2.5 py-1.5 focus:border-fuchsia-400 focus:outline-none disabled:bg-slate-50';

export const OffenePunkteReiter: React.FC<Props> = ({ bundle, ctx, gesperrt, schreiben }) => {
    const o = bundle.ordnung;
    if (!o) return null;
    const luecken = sortiereLuecken(bundle.luecken);
    const offen = luecken.filter(l => !l.erledigt_am).length;

    const setze = (l: Luecke, patch: Partial<Luecke>) =>
        schreiben(() => service.updateLuecke(l.id, patch, ctx));

    const heute = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    return (
        <div className="space-y-4 max-w-4xl">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-bold text-slate-800">
                        Offene Punkte {offen > 0 && <span className="text-amber-600">({offen} offen)</span>}
                    </h3>
                    <p className="text-[12px] text-slate-500">
                        Ein offener <strong>Pflichtpunkt</strong> sperrt die Freigabe. Er erscheint im Dokument
                        an seiner Textstelle und in der Sammelliste.
                    </p>
                </div>
                {!gesperrt && (
                    <button
                        onClick={() => schreiben(() => service.addLuecke(
                            o, naechsteLueckenNummer(bundle.luecken), { punkt: '' }, ctx,
                        ))}
                        className="text-[12px] font-bold text-fuchsia-600 hover:text-fuchsia-700 flex items-center gap-1"
                    >
                        <Plus className="w-3.5 h-3.5" /> Punkt hinzufügen
                    </button>
                )}
            </div>

            {!luecken.length && (
                <p className="text-[12px] text-slate-400 italic border border-dashed border-slate-200 rounded-lg px-3 py-5 text-center">
                    Keine offenen Punkte. Im Dokument steht dann der Satz „keine offenen Punkte" —
                    und keine leere Tabelle.
                </p>
            )}

            {luecken.map(l => (
                <div key={l.id}
                    className={`border rounded-xl p-3 ${l.erledigt_am ? 'bg-slate-50 border-slate-200 opacity-70' : 'bg-white border-amber-200'}`}
                >
                    <div className="flex items-start gap-2">
                        <span className="text-[11px] font-black text-slate-400 tabular-nums mt-2 w-8 shrink-0">
                            {l.nr}
                        </span>
                        <EntwurfInput
                            className={`${eingabe} flex-1`} placeholder="Was fehlt?" wert={l.punkt}
                            disabled={gesperrt} aufSpeichern={v => setze(l, { punkt: v })}
                        />
                    </div>
                    <div className="flex items-center gap-2 mt-2 pl-10 flex-wrap">
                        <EntwurfInput className={`${eingabe} w-40`} placeholder="Verantwortlich" wert={l.verantwortlich}
                            disabled={gesperrt} aufSpeichern={v => setze(l, { verantwortlich: v })} />
                        <input type="date" className={`${eingabe} w-36`} value={l.frist ?? ''}
                            disabled={gesperrt} onChange={e => setze(l, { frist: e.target.value || null })} />
                        <label className="text-[12px] text-slate-500 flex items-center gap-1.5">
                            <input type="checkbox" checked={l.pflicht} disabled={gesperrt}
                                onChange={e => setze(l, { pflicht: e.target.checked })} />
                            Pflicht
                        </label>
                        <div className="flex-1" />
                        {!gesperrt && (
                            <>
                                <button
                                    onClick={() => setze(l, { erledigt_am: l.erledigt_am ? null : heute() })}
                                    className={`text-[12px] font-bold rounded-md px-2.5 py-1 flex items-center gap-1 ${
                                        l.erledigt_am ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                    }`}
                                >
                                    <Check className="w-3.5 h-3.5" />
                                    {l.erledigt_am ? `erledigt ${l.erledigt_am}` : 'erledigt'}
                                </button>
                                <button onClick={() => schreiben(() => service.deleteLuecke(l.id, ctx))}
                                    className="text-slate-300 hover:text-rose-500" title="Punkt löschen">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};
